import { TmsErrorCode, TransportMode } from '@b-stock/shipment-api-client'

import {
  PARCEL_NO_RATES_ERROR_CODES,
  type ParcelNoRatesErrorCode,
  type ParcelQuote,
} from './types'

const isKnownErrorCode = (code: TmsErrorCode): code is ParcelNoRatesErrorCode =>
  (PARCEL_NO_RATES_ERROR_CODES as readonly TmsErrorCode[]).includes(code)

/**
 * FP-1906: detect the backend's "no usable parcel rate" condition.
 *
 * The backend (FP-1903) signals this not with an empty array but with a single
 * zero-cost placeholder quote (`totalPrice === 0`) carrying
 * `tmsMetaData.errorCode`. We treat the set as blocked when ANY quote carries
 * an `errorCode`, or when a `totalPrice === 0` quote does — and map an
 * unknown/unmapped code to the `NO_RATES_FOUND` copy so we always fail closed
 * with friendly canned text.
 *
 * Returns the resolved {@link ParcelNoRatesErrorCode} when the set is blocked,
 * otherwise `null`.
 */
export const detectParcelNoRatesCode = (
  quotes: ParcelQuote[] | undefined | null
): ParcelNoRatesErrorCode | null => {
  if (!quotes || quotes.length === 0) return null

  for (const quote of quotes) {
    const errorCode = quote.tmsMetaData?.errorCode
    if (!errorCode) continue
    // Only zero-cost quotes block payment; a priced quote with an incidental
    // code is still usable, so don't block on it.
    if (quote.totalPrice !== 0) continue
    return isKnownErrorCode(errorCode) ? errorCode : TmsErrorCode.NoRatesFound
  }

  return null
}

/**
 * The no-rates code for a single selected quote when it is a parcel
 * zero-quote, otherwise `null`. Lets the listing page give the same reason as
 * the Bid and Buy Now pop-ups.
 */
export const getParcelZeroQuoteCode = (
  quote: ParcelQuote | undefined | null
): ParcelNoRatesErrorCode | null =>
  quote?.transportMode === TransportMode.Parcel
    ? detectParcelNoRatesCode([quote])
    : null
